import type { AppDispatch } from '@redux/store'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload as UseFormBasePayload } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as templateGroupActions from '@redux/modules/templateGroup'

import defaultFormState, { requiredFields } from '@models/templateGroup'

import { setTimeToDayEnd } from '@functions/dateTime'

import type { TemplateGroupModuleState, EntityOptions } from '@redux/modules/types'
import type { TemplateGroupModel } from '@models/templateGroup'

import { generateUrls as generateTaskListUrls } from '@hooks/useTaskList'

type EntityActionFunctionParams = {
  entityParams: Partial<TemplateGroupModel>,
  dispatch: AppDispatch,
  requestOptions: EntityOptions,
}

const createTemplateGroup = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { createTemplateGroup: createFn } = templateGroupActions

  // @ts-expect-error TS2345
  return dispatch(createFn(entityParams, requestOptions))
}

const getTemplateGroupWithProjectRedirectUrl = (params: { data: TemplateGroupModel }) => {
  const { data } = params

  const taskList = data?.task_lists?.[0]
  if (!taskList) return ''

  const { editTaskListUrl } = generateTaskListUrls(taskList)

  return editTaskListUrl
}

const createTemplateGroupWithProject = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { createTemplateGroupWithProject: createFn } = templateGroupActions

  const updatedParams = {
    ...entityParams,
    // @ts-expect-error TS2339
    due_date: setTimeToDayEnd({ dateString: entityParams.due_date }),
  }

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      return {
        success,
        data,
        redirectUrl: getTemplateGroupWithProjectRedirectUrl({ data }),
      }
    }

    return response
  })
}

const updateTemplateGroup = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { updateTemplateGroup: updateFn } = templateGroupActions

  // @ts-expect-error TS2345
  return dispatch(updateFn(entityParams, requestOptions))
}

const deleteTemplateGroup = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { deleteTemplateGroup: deleteFn } = templateGroupActions

  // @ts-expect-error TS2554
  return dispatch(deleteFn(entityParams, requestOptions))
}

type UseFormPayload = UseFormBasePayload & {
  creating?: boolean,
  updating?: boolean,
}

export function useTemplateGroupForm(
  templateGroup: Partial<TemplateGroupModel>,
  options?: UseFormOptions & { defaultFormStateParams?: object },
): UseFormPayload {
  const { defaultFormStateParams, validateOn } = options || {}

  const { creating, updating } = useSelector(reduxState => reduxState.templateGroups)

  const templateGroupForm = useForm(
    { ...defaultFormState, ...defaultFormStateParams },
    { entity: templateGroup, requiredFields: [...requiredFields], validateOn },
    [templateGroup.id, templateGroup.cache_key],
  )

  return {
    ...templateGroupForm,
    creating,
    updating,
  }
}

export const useRelations = (entity: Partial<TemplateGroupModel> = {}) => {
  const { brand_id } = entity

  const { brands } = useSelector(reduxState => reduxState.entities)

  const brand = brand_id ? brands[brand_id] : {}

  return {
    brand,
  }
}

interface UseTemplateGroupOptions {
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

type UseTemplateGroupPayload = {
  callbacks: {
    createTemplateGroup: (entityParams: Partial<TemplateGroupModel>, requestOptions: EntityOptions) => void,
    createTemplateGroupWithProject: (entityParams: Partial<TemplateGroupModel>, requestOptions: EntityOptions) => void,
    deleteTemplateGroup: (entityParams: Partial<TemplateGroupModel>, requestOptions: EntityOptions) => void,
    updateTemplateGroup: (entityParams: Partial<TemplateGroupModel>, requestOptions: EntityOptions) => void,
  },
  templateGroup: Partial<TemplateGroupModel>,
  loading: boolean,
}

function useTemplateGroup(
  initEntity: Partial<TemplateGroupModel> = {},
  options: UseTemplateGroupOptions = {},
): UseTemplateGroupPayload {
  const { performHttpRequests, requestOptions } = options || {}

  // @ts-expect-error TS2345
  const { entity: templateGroup } = useLatestEntity(initEntity, 'templateGroups')
  const { id } = templateGroup

  const dispatch = useDispatch()

  // @ts-expect-error TS2339
  const { relatedEntity } = useRelations(templateGroup)

  const { loading } = useSelector(reduxState => reduxState.templateGroups)

  useReduxAction(
    'templateGroups',
    'loadTemplateGroup',
    {
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, reqOptions: EntityOptions) => action(id, reqOptions),
      // @ts-expect-error
      shouldPerformFn: (entityReducer: TemplateGroupModuleState) => {
        const { loadedIds } = entityReducer
        // @ts-expect-error TS2345
        return performHttpRequests && !loadedIds?.includes(id)
      },
    },
  )

  return {
    callbacks: {
      createTemplateGroup: (entityParams: Partial<TemplateGroupModel>, entityRequestOptions: EntityOptions) => (
        createTemplateGroup({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      createTemplateGroupWithProject: (entityParams: Partial<TemplateGroupModel>, entityRequestOptions: EntityOptions) => (
        createTemplateGroupWithProject({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      deleteTemplateGroup: (entityParams: Partial<TemplateGroupModel>, entityRequestOptions: EntityOptions) => (
        deleteTemplateGroup({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      updateTemplateGroup: (entityParams: Partial<TemplateGroupModel>, entityRequestOptions: EntityOptions) => (
        updateTemplateGroup({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
    },
    // @ts-expect-error TS2322
    templateGroup,
    loading,
  }
}

export default useTemplateGroup
